import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ReturnItemModel,
  ReturnModel,
  ReturnStatus,
} from "../../Api/Models/return.model";
import { RiderProfileLayoutContextInterface } from "../../Pages/Rider/RiderProfileLayout";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useLoading } from "../../Context/LoadingProvider";
import { returnService } from "../../Api/Services/ReturnService";
import { LaravelValidationError } from "../../Api/request";
import { riderService } from "../../Api/Services/RiderService";
import { RiderModel } from "../../Api/Models/rider.model";
import ImageInput from "../ImageInput";
import { CheckIcon } from "../Icons/CheckIcon";
import { CalendarIcon } from "../Icons/CalendarIcon";
import { ModalSuccessIcon } from "../Icons/ModalSuccessIcon";
import { MopedIcon } from "../Icons/MopedIcon";
import { UserLineIcon } from "../Icons/UserLineIcon";
import { TickIcon } from "../Icons/TickIcon";
import { ChangeIcon } from "../Icons/ChangeIcon";
import { WarehouseIcon} from "../Icons/WarehouseIcon";

interface ReturnProps {
  rider: RiderModel;
  updateGears: CallableFunction;
}

const Return: React.FunctionComponent<ReturnProps> = ({
  rider,
  updateGears,
}) => {
  const { startLoading, finnishLoading } = useLoading();
  const [returnData, setReturnData] = useState<ReturnModel | null>(null);

  console.log("ReturnData", returnData);
  const originalReturnData = useRef<ReturnModel | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [clearancePapers, setClearancePapers] = useState<File[]>([]);

  const { showInfo } = useInfoModal();

  const { fetchRider } = useOutletContext<RiderProfileLayoutContextInterface>();

  const fetchReturn = () => {
    startLoading("get-profile-return");
    riderService
        .getReturn(rider.riderId)
        .then((returnData) => {
          originalReturnData.current = { ...returnData };
          returnData.items = returnData.items.map((item) => {
            return {
              ...item,
              savedToDb:
                  item.status === ReturnStatus.GOOD ||
                  item.status === ReturnStatus.UNUSABLE ||
                  item.status === ReturnStatus.MISSING,
              transferRiderId: null,
            };
          });

          setReturnData({ ...returnData });
        })
        .finally(() => {
          finnishLoading("get-profile-return");
        });
  };


  useEffect(() => {
    fetchReturn();
    //eslint-disable-next-line
  }, []);

  const updateReturnItemStatus = (itemId: number, status: ReturnStatus, transferRiderId?:string|null) => {
    if (returnData) {
      const itemsState = returnData.items;

      let itemToUpdate = itemsState.find((x) => x.id === itemId);

      if (itemToUpdate) {
        if (itemToUpdate.status === status) {
          itemToUpdate.status = ReturnStatus.UNKNOWN;
        } else {
          itemToUpdate.status = status;
        }

        if (status === ReturnStatus.TRANSFER && transferRiderId) {
          itemToUpdate.transferRiderId = transferRiderId;
        }
        else
        {
          itemToUpdate.transferRiderId = null;
        }

        setReturnData({ ...returnData, items: itemsState });
      }
    }
  };

  const handleReturnSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!returnData) {
      return;
    }

    if (
      images.length < 1 ||
      (!returnData.items.some((x) => x.status === ReturnStatus.UNKNOWN) &&
        clearancePapers.length < 1)
    ) {
      showInfo(t("Please select an image!"));
      return;
    }

    startLoading("update-return-data");
    let formData = new FormData();
    formData.append("return_id", returnData.id.toString());
    formData.append(`image`, images[0]);
    if (typeof clearancePapers[0] !== "undefined") {
      formData.append(`clearance_papers`, clearancePapers[0]);
    }

    returnData.items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id.toString());
      formData.append(`items[${index}][status]`, item.status.toString());
      if (item.status === ReturnStatus.TRANSFER) {
        formData.append(`items[${index}][transferRiderId]`, item.transferRiderId || "");
      }
    });

    returnService
      .update(formData)
      .then((updatedReturnData) => {
        originalReturnData.current = { ...updatedReturnData };
        updatedReturnData.items = updatedReturnData.items.map((item) => {
          return {
            ...item,
            savedToDb:
              item.status === ReturnStatus.GOOD ||
              item.status === ReturnStatus.UNUSABLE ||
              item.status === ReturnStatus.MISSING,
          };
        });
        setReturnData(updatedReturnData);
        fetchRider();
        fetchReturn();
        updateGears();
        //showInfo(<>{t("Gear return updated!")}</>);
        showInfo(
          <>
            <div className="flex justify-center mb-2"><ModalSuccessIcon /></div> 
            <div className="text-center">{t("All done!")}</div>
            <div className="text-center text-secondary">{t("Gear return updated!")}</div>
          </>
        );
      })
      .catch((error: LaravelValidationError) => {
        if (error.message) {
          showInfo(error.message);
        }
      })
      .finally(() => {
        finnishLoading("update-return-data");
      });
  };

  const getReturnStatusLabel = (status: ReturnStatus): string => {
    const labels: Record<ReturnStatus, string> = {
      [ReturnStatus.UNKNOWN]: t("Unknown"),
      [ReturnStatus.GOOD]: t("New"),
      [ReturnStatus.MISSING]: t("Missing"),
      [ReturnStatus.UNUSABLE]: t("Dispose"),
      [ReturnStatus.REUSABLE]: t("Reusable"),
      [ReturnStatus.TRANSFER]: t("Transfer"),
      [ReturnStatus.BACKTOTPL]: t("BackToTPL"),
    };

    return labels[status] || t("Unknown");
  };

  const [selectedTransferItem, setSelectedTransferItem] = useState<ReturnItemModel | null>(
      null
  );

  const [searchId, setSearchId] = useState<string>("");
  const [riderName, setRiderName] = useState<string | null>(null);
  const [isFound, setIsFound] = useState<boolean>(false);

  const handleSearch = async () => {
    if (!selectedOption.transferRiderId) {
      setRiderName(null);
      setIsFound(false);
      return;
    }
    console.log("Search ID:", searchId);

    startLoading("search-rider");
    try {
      const result = await riderService.search(selectedOption.transferRiderId);
      if (result && result.length > 0) {
        if(result[0].tpl_name !== rider.tpl_name) {
          console.log("Rider is not in the same tpl");
          setRiderName(t("not_same_tpl"));
          setIsFound(false);
        }
        else
        {
          if(result[0].status === 3)
          {
            console.log("Rider is inactive");
            setRiderName(t("id_inactive"));
            setIsFound(false);
          }
          else
          {
            setRiderName(result[0].name);
            setIsFound(true);
          }
        }


      } else {
        setRiderName(null);
        setIsFound(false);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setRiderName(null);
      setIsFound(false);
    } finally {
      finnishLoading("search-rider");
    }
  };


  const [selectedOption, setSelectedOption] = useState<{
    type: "TRANSFER" | "REUSABLE" | "BACKTOTPL" | null;
    transferRiderId: string | null;
  }>({
    type: "TRANSFER",
    transferRiderId: null,
  });

  const getStatusCounts = (): Record<ReturnStatus, number> => {
    if (!returnData) return {
      [ReturnStatus.UNKNOWN]: 0,
      [ReturnStatus.GOOD]: 0,
      [ReturnStatus.MISSING]: 0,
      [ReturnStatus.UNUSABLE]: 0,
      [ReturnStatus.REUSABLE]: 0,
      [ReturnStatus.TRANSFER]: 0,
      [ReturnStatus.BACKTOTPL]: 0,
    };

    const counts: Record<ReturnStatus, number> = {
      [ReturnStatus.UNKNOWN]: 0,
      [ReturnStatus.GOOD]: 0,
      [ReturnStatus.MISSING]: 0,
      [ReturnStatus.UNUSABLE]: 0,
      [ReturnStatus.REUSABLE]: 0,
      [ReturnStatus.TRANSFER]: 0,
      [ReturnStatus.BACKTOTPL]: 0,
    };

    returnData.items.forEach((item) => {
      if (
          item.status === ReturnStatus.REUSABLE ||
          item.status === ReturnStatus.BACKTOTPL ||
          item.status === ReturnStatus.TRANSFER
      ) {
        counts[ReturnStatus.TRANSFER] += 1;
      } else {
        counts[item.status] += 1;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

    const isOlderThan7Days = (dateString: string): boolean => {
        const assignDate = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - assignDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays > 7;
    };


    if (!returnData) {
    return <></>;
  }

  return (
    <>
      <div className="heading mb-4">{t('Gear owned by the rider')}</div>
      <form className="" onSubmit={handleReturnSave}>
        {returnData?.items.map((item, index) => {
          return (
              <div
                  className=""
                  key={index}
              >

                <div className="product-list-card mb-4" key={index}>
                  <div className="flex gap-2 mb-2">
                    <div className="relative">
                      <div className="product-list-card__image">
                        <img src={item.productImage} alt="package name"/>
                      </div>
                      {item.variation && (
                          <div className="product-list-card__variation-name">
                            {item.variation}
                          </div>
                      )}

                    </div>

                    <div className="flex flex-col justify-between w-full">
                      <div className="flex justify-between gap-2">
                        <div className="product-list-card__title">
                          {item.productName}
                        </div>
                        <div></div>
                      </div>

                      <div className="flex justify-between gap-2 ">

                        <div className="flex gap-1 items-center color-secondary">
                          <CalendarIcon/> {item.originalAssignDate}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {[
                      ReturnStatus.UNUSABLE,
                      ReturnStatus.GOOD,
                      ReturnStatus.MISSING,
                    ].map((status) => (
                        <button
                            type="button"
                            className={"check-rectangle w-full " +
                                (item.status === status
                                    ? "checked"
                                    : "")
                            }
                            onClick={() => {
                              if (!item.savedToDb) {
                                updateReturnItemStatus(item.id, status);
                              }
                            }}
                            disabled={
                                item.savedToDb ||
                                (status === ReturnStatus.REUSABLE && !item.reusePossible) ||
                                (status === ReturnStatus.GOOD && isOlderThan7Days(item.originalAssignDate))
                            }
                        >

                          {getReturnStatusLabel(status)}

                          {item.status === status ? (
                              <div className="check-rectangle__marker">
                                <CheckIcon/>
                              </div>
                          ) : null}
                        </button>
                    ))}

                    <button
                        type="button"
                        onClick={() => {
                          setSelectedTransferItem(item);
                          setSelectedOption({
                            type: item.status === ReturnStatus.TRANSFER
                                ? "TRANSFER"
                                : item.status === ReturnStatus.REUSABLE
                                    ? "REUSABLE"
                                    : item.status === ReturnStatus.BACKTOTPL
                                        ? "BACKTOTPL"
                                        : "TRANSFER",
                            transferRiderId: item.status === ReturnStatus.TRANSFER ? item.transferRiderId || null : null,
                          });

                          if (item.status === ReturnStatus.TRANSFER && item.transferRiderId) {
                            setSearchId(item.transferRiderId);
                            setRiderName(riderName || null);
                            setIsFound(true);
                          } else {
                            setSearchId("");
                            setRiderName(null);
                            setIsFound(false);
                          }
                        }}
                        className={
                            "check-rectangle w-full " +
                            (item.status === ReturnStatus.REUSABLE ||
                            item.status === ReturnStatus.TRANSFER ||
                            item.status === ReturnStatus.BACKTOTPL
                                ? "checked"
                                : "")
                        }
                    >
                      {t("Transfer")}

                      {(item.status === ReturnStatus.REUSABLE ||
                          item.status === ReturnStatus.TRANSFER ||
                          item.status === ReturnStatus.BACKTOTPL) && (
                          <div className="check-rectangle__marker">
                            <CheckIcon/>
                          </div>
                      )}
                    </button>


                  </div>

                </div>

              </div>
          );
        })}

        <div className="separator-line" style={{paddingTop: '0'}}></div>

        <div className="mb-2">{t("Selected items")}:</div>

        <div className="flex gap-2 flex-wrap">
          {[
            {status: ReturnStatus.GOOD, label: t("New")},
            {status: ReturnStatus.MISSING, label: t("Missing")},
            {status: ReturnStatus.UNUSABLE, label: t("Dispose")},
            {status: ReturnStatus.TRANSFER, label: t("Transfer")},
          ].map(({status, label}) => (
              <div
                  key={status}
                  className={`variation-pcs-info ${
                      statusCounts[status] ? "variation-pcs-info--marked" : ""
                  }`}
              >
                <div className="variation-pcs-info__quantity">{label}</div>
                <div className="variation-pcs-info__size">
                  {statusCounts[status] || 0}
                </div>
              </div>
          ))}
        </div>


        <div className="mb-4"></div>


        {returnData?.items
            .map((currentItem) => {
              let ogItem: ReturnItemModel | undefined =
                  originalReturnData?.current?.items.find(
                      (x) => x.id === currentItem.id
                  );

              if (!ogItem) {
                return false;
              }

              return (
                  currentItem.status !== ogItem.status ||
                  currentItem.condition !== ogItem.condition ||
                  currentItem.note !== ogItem.note
              );
            })
            .some((x) => x === true) && (
            <>
              <div className="mb-4">
                <ImageInput
                    images={images}
                    setImages={setImages}
                    preview={true}
                    key={"asdhg45zt54"}
                    infoText={t("Picture of items")}
                />
              </div>

              <div className="mb-6">
                <ImageInput
                    images={clearancePapers}
                    setImages={setClearancePapers}
                    preview={true}
                    key={"asdhg45"}
                    required={
                      !returnData.items.some(
                          (x) => x.status === ReturnStatus.UNKNOWN
                      )
                    }
                    infoText={t("Clearance paper")}
                />
              </div>


              <button type="submit" className="button w-full">
                {t("Save")}
              </button>
            </>
        )}
      </form>

      {selectedTransferItem && (
          <div className="bottom-modal" tabIndex={-1} role="dialog">
            <div className="bottom-modal__scroll-content">
              <div className="bottom-modal__body" style={{ marginTop: "50px" }}>
                <div className="stock-item__image">
                  <img
                      src={selectedTransferItem!.productImage ?? ""}
                      alt="Product"
                      style={{ height: "75px" }}
                  />
                </div>

                <div className="stock-item__title heading text-center mb-2 mt-8">
                  {t("Transfer item")}
                </div>

                <div className="text-secondary text-center mb-6">
                  {selectedTransferItem.productName}
                </div>

                <div className="checkboxlist mb-8">
                  <div
                      className={`checkboxlist-item ${
                          selectedOption.type === "TRANSFER" ? "checked" : ""
                      }`}
                      onClick={() =>
                          setSelectedOption({
                            type: "TRANSFER",
                            transferRiderId: selectedOption.transferRiderId,
                          })
                      }
                  >
                    <div className="flex gap-2 justify-between">
                      <div className={`checkboxlist-item__text ${selectedTransferItem?.reusePossible || selectedTransferItem?.reused ? "" : "disabled"}`}>
                        <MopedIcon width={20} height={18}/> {t("Transfer to another rider")}
                      </div>
                      {selectedOption.type === "TRANSFER" && (
                          <div className="checkboxlist-item__selector">
                            <TickIcon/>
                          </div>
                      )}
                    </div>

                    {selectedOption.type === "TRANSFER" && (
                        <div className="checkboxlist-item__description">
                          <div className="text-secondary mb-2">{t("Rider ID")}</div>
                          <div className="relative">
                            <UserLineIcon className="input-icon"/>
                            <input
                                type="number"
                                placeholder={t("Enter Rider ID") as string}
                                required
                                className={`input-field ${isFound ? "input-success" : ""} ${!selectedTransferItem?.reusePossible ? "disabled" : ""}`}
                                value={selectedOption.transferRiderId || ""}
                                onChange={(e) =>
                                    setSelectedOption({
                                      ...selectedOption,
                                      transferRiderId: e.target.value,
                                    })
                                }
                                onBlur={handleSearch}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                disabled={!selectedTransferItem?.reusePossible}
                            />
                            {riderName && (
                                <span className="absolute top-1/2 transform -translate-y-1/2 left-28 text-secondary">
                      {`(${riderName})`}
                    </span>
                            )}
                          </div>
                        </div>
                    )}
                  </div>

                  {(rider.tplHasWH ? (
                      <div
                          className={`checkboxlist-item ${selectedOption.type === "BACKTOTPL" ? "checked" : ""} 
      ${selectedTransferItem?.reusePossible || selectedTransferItem?.reused ? "" : "disabled"}`}
                          onClick={() => {
                            if (!selectedTransferItem?.reusePossible && !selectedTransferItem?.reused) {
                              return;
                            }
                            setSelectedOption({
                              type: "BACKTOTPL",
                              transferRiderId: null,
                            });
                          }}
                      >
                        <div className="flex gap-2 justify-between">
                          <div
                              className={`checkboxlist-item__text ${selectedTransferItem?.reusePossible || selectedTransferItem?.reused ? "" : "disabled"}`}
                          >
                            <WarehouseIcon width={18} height={18} /> {t("Back to 3PL stock")}
                          </div>
                          {selectedOption.type === "BACKTOTPL" && (
                              <div className="checkboxlist-item__selector">
                                <TickIcon />
                              </div>
                          )}
                        </div>
                      </div>
                  ) : null)}



                  <div
                      className={`checkboxlist-item ${
                          selectedOption.type === "REUSABLE" ? "checked" : ""
                      } ${selectedTransferItem?.reusePossible ? "" : "disabled"}`}
                      onClick={() => {
                        console.log("reusePossible értéke:", selectedTransferItem?.reusePossible);
                        if (!selectedTransferItem?.reusePossible) {
                          return;
                        }
                        setSelectedOption({
                          type: "REUSABLE",
                          transferRiderId: null,
                        });
                      }}
                  >
                    <div className="flex gap-2 justify-between">
                      <div
                          className={`checkboxlist-item__text ${selectedTransferItem?.reusePossible ? "" : "disabled"}`}>
                        <ChangeIcon width={18} height={18}/> {t("Item can be reused")}
                      </div>
                      {selectedOption.type === "REUSABLE" && (
                          <div className="checkboxlist-item__selector">
                            <TickIcon/>
                          </div>
                      )}
                    </div>
                  </div>


                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <div
                        className="button button--light w-full"
                        onClick={() => {
                          setSelectedTransferItem(null);
                          setSearchId("");
                          setRiderName(null);
                          setIsFound(false);
                          setSelectedOption({type: "TRANSFER", transferRiderId: null});
                        }}
                    >
                      {t("Back")}
                    </div>
                  </div>
                  <div className="flex-1">
                    <button
                        className={`button w-full ${
                            (selectedOption.type === "TRANSFER" && isFound) ||
                            selectedOption.type === "REUSABLE" ||
                            selectedOption.type === "BACKTOTPL"
                                ? ""
                                : "button--disabled"
                        }`}
                        onClick={() => {
                          if (selectedOption.type === "TRANSFER" && isFound) {
                            updateReturnItemStatus(selectedTransferItem.id, ReturnStatus.TRANSFER, selectedOption.transferRiderId);
                          } else if (selectedOption.type === "REUSABLE") {
                            updateReturnItemStatus(selectedTransferItem.id, ReturnStatus.REUSABLE);
                          } else if (selectedOption.type === "BACKTOTPL") {
                            updateReturnItemStatus(selectedTransferItem.id, ReturnStatus.BACKTOTPL);
                          }

                          setSelectedTransferItem(null);
                          setSearchId("");
                          setRiderName(null);
                          setIsFound(false);
                          setSelectedOption({type: "TRANSFER", transferRiderId: null});
                        }}
                        disabled={
                          !(
                              (selectedOption.type === "TRANSFER" && isFound) ||
                              selectedOption.type === "REUSABLE" ||
                              selectedOption.type === "BACKTOTPL"
                          )
                        }
                    >
                      {t("Save")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}
    </>
  );
};

export default Return;
